import { useState, useMemo } from 'react';
import {
  Modal, Stack, Select, TextInput, Button, Group, CopyButton,
  Text, Divider, ActionIcon, Tooltip, SegmentedControl,
} from '@mantine/core';
import { IconCopy, IconCheck } from '@tabler/icons-react';
import { PlaylistLinker } from '../PlaylistLinker';
import { Hostname } from '@lib2/model/common/ServiceHosts';
import { EMBED_SIZES, type EmbedSize } from '@/lib/constants/embed';
import { usePlayers, setStoredPlayerId } from '@/features/players/usePlayers';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { AnyPlaylistData } from '@/lib/types/Playlist';

interface PlaylistPublishModalProps {
  opened: boolean;
  onClose: () => void;
  playlist: AnyPlaylistData;
}

const SIZE_OPTIONS = Object.entries(EMBED_SIZES).map(([key, val]) => ({
  value: key,
  label: val.label,
}));

const MONO_STYLE = { input: { fontFamily: 'monospace', fontSize: 12 } } as const;

/** Read-only URL field with integrated one-click copy button. */
function CopyableField({ label, value }: { label: string; value: string }) {
  return (
    <TextInput
      label={label}
      value={value}
      readOnly
      styles={MONO_STYLE}
      rightSectionWidth={36}
      rightSection={
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="left">
              <ActionIcon
                variant="subtle"
                color={copied ? 'teal' : 'gray'}
                onClick={copy}
                size="sm"
              >
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      }
    />
  );
}

export function PlaylistPublishModal({ opened, onClose, playlist }: PlaylistPublishModalProps) {
  const { selectedCId } = useAuth();
  const { defaultPlayerId, playerOptions } = usePlayers();

  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [sizeKey, setSizeKey] = useState<EmbedSize>('medium');
  const [sizeMode, setSizeMode] = useState<'responsive' | 'fixed'>('responsive');

  const effectivePlayerId = selectedPlayerId ?? defaultPlayerId;

  const urls = useMemo(() => {
    if (!effectivePlayerId) return null;

    const size = EMBED_SIZES[sizeKey];
    const params = {
      cId: playlist.cId,
      playerId: effectivePlayerId,
      listId: playlist.listId,
      listType: playlist.listType,
      mediaType: playlist.mediaType,
      isReseller: Hostname.isResellerHostname(),
    };

    return {
      embedCode: PlaylistLinker.getIframeHtml(
        params,
        sizeMode === 'responsive',
        { width: size.width, height: size.height },
        { preview: true },
      ),
      previewUrl: PlaylistLinker.getPreviewUrl(params, { preview: true }),
    };
  }, [effectivePlayerId, playlist, sizeKey, sizeMode]);

  const handlePlayerChange = (val: string | null) => {
    if (!val) return;
    const id = parseInt(val, 10);
    setSelectedPlayerId(id);
    if (selectedCId) setStoredPlayerId(selectedCId, id);
  };

  if (!effectivePlayerId || !urls) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Publish & Embed"
      size="lg"
    >
      <Stack gap="md">
        {/* Player selector */}
        <Select
          label="Player"
          data={playerOptions}
          value={String(effectivePlayerId)}
          onChange={handlePlayerChange}
          allowDeselect={false}
        />

        {/* Embed sizing */}
        <Stack gap="xs">
          <Text size="sm" fw={500}>Embed Size</Text>
          <Group gap="md" align="center">
            <SegmentedControl
              value={sizeMode}
              onChange={(val) => setSizeMode(val as 'responsive' | 'fixed')}
              data={[
                { value: 'responsive', label: 'Responsive' },
                { value: 'fixed', label: 'Fixed Size' },
              ]}
              size="sm"
            />
            {sizeMode === 'fixed' && (
              <Select
                data={SIZE_OPTIONS}
                value={sizeKey}
                onChange={(val) => val && setSizeKey(val as EmbedSize)}
                allowDeselect={false}
                w={220}
                size="sm"
              />
            )}
          </Group>
        </Stack>

        {/* Embed code */}
        <CopyableField label="Embed Code" value={urls.embedCode} />

        <Divider />

        <Text size="sm" fw={600} c="dimmed">Links</Text>

        <CopyableField label="Preview URL" value={urls.previewUrl} />

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
